import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, Signal, PLATFORM_ID, ViewChild, inject, TemplateRef, input, effect, Input, signal } from '@angular/core';
import { AvatarModule } from 'primeng/avatar';
import { MenubarModule } from 'primeng/menubar';
import { ApiService } from '../../../services/api.service';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { MenuModule } from 'primeng/menu';
import { PanelMenuModule } from 'primeng/panelmenu';
import { isPlatformBrowser } from '@angular/common';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthService, LOGIN_TYPE_REGISTER, LOGIN_TYPE_TOPBAR } from '../../../services/authentication/auth.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faBookmark, faCalendarXmark, faComment, faCreditCard, faHome, faLock, faMapMarkerAlt, faSearch, faShoppingCart, faSignOut, faUser, faWallet, faGavel, faTicket } from '@fortawesome/free-solid-svg-icons'
import { faFacebook, faLinkedin, faXTwitter } from '@fortawesome/free-brands-svg-icons'
import { AbsoluteMediaUriService } from '../../../services/absolute-media-uri.service';
import { environment } from '../../../../environments/environment';
import { DateFormatService } from '../../../services/date-format/date-format.service';
import { WelcomeBackService } from '../../../services/welcome-back/welcome-back.service';
import { AbsoluteWebUrlService } from '../../../services/absolute-web-url/absolute-web-url.service';
import { ShoppingCartComponent } from "../../shopping-cart/shopping-cart/shopping-cart.component";
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    MenubarModule,
    AvatarModule,
    RouterModule,
    ButtonModule,
    MenuModule,
    PanelMenuModule,
    DynamicDialogModule,
    FontAwesomeModule,
    ShoppingCartComponent,
],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit {
  protected readonly absoluteMediaUriService = inject(AbsoluteMediaUriService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly authService = inject(AuthService);
  private readonly apiService = inject(ApiService);
  private readonly welcomeBackService = inject(WelcomeBackService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly dateFormatService = inject(DateFormatService);
  protected readonly absoluteWebUrlService = inject(AbsoluteWebUrlService);


  @ViewChild('userMenuHeader') userMenuHeader!: TemplateRef<any>;

  public id = input(0);
  public hideShoppingCart = input(false);
  @Input() shareSocialMediaData?: { type: string; id: number };
  public shareTextInput = input("");
  public showSocial = input(true);
  public showSocialShare = input(true);
  protected icons = {
    faUser: faUser,
    faCalendarXmark: faCalendarXmark,
    faFacebook: faFacebook,
    faLinkedin: faLinkedin,
    faXTwitter: faXTwitter,
    faHome: faHome,
    faBookmark: faBookmark,
    faLock: faLock,
    faMapMarker: faMapMarkerAlt,
    faCreditCard: faCreditCard,
    faSearch: faSearch,
    faComment: faComment,
    faShoppingCart: faShoppingCart,
    faWallet: faWallet,
    faSignOut: faSignOut,
    faGavel: faGavel,
    faTicket: faTicket,
  }

  isAuthenticated = computed(() => {
    return this.oidcSecurityService.authenticated().isAuthenticated;
  });

  auctionConfigSignal = this.apiService.auctionConfigSignal;

  userName: Signal<string> = computed(() => {
    if (this.isAuthenticated()) {
      const userData = this.oidcSecurityService.userData();
      if (userData.userData != null && userData.userData.name != undefined) {
        return userData.userData.name;
      }
    }
    return "";
  });

  constructor() {
    effect(() => {
      if (isPlatformBrowser(this.platformId)) {
        const homePageData = this.homePageData();
        if (homePageData != null) {
          document.title = homePageData.orgName + " | BiddingForGood";
        }
      }
    });
  }
  ngOnInit(): void {
    if (this.homePageData()?.auctionId != this.id()) {
      this.apiService.getAuctionHomePageData('auctions', this.id());
    }

    if (!this.auctionConfigSignal()) {
      this.apiService.getAuctionConfig(this.id()).subscribe();
    }
  }

  initials = computed(() => {
    return this.userName()
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase();
  });

  //signal with home page information
  homePageData = computed(() => this.apiService.homePageSignal());

  protected showBfgLogo = computed(() => {
    let homePageData = this.homePageData();
    if (homePageData != null) {
      return !homePageData.whiteLabelEvent;
    }
    return true;
  });

  //transform date
  auctionEndDateFormat = computed(() => {
    const timezone = this.auctionConfigSignal()?.timezone || 'UTC';
    return this.dateFormatService.formatDateWithTimezone(
      timezone,
      this.homePageData()?.auctionEndDate || ''
    );
  });

  protected baseShareUrl = computed(() => {
    if (isPlatformBrowser(this.platformId) && this.shareSocialMediaData) {
      const { type, id } = this.shareSocialMediaData;
      return `${environment.apiUrl}/v3/social-media-sharing/${type}/${id}`;
    }
    return "";
  })


  protected facebookShareUrl = computed(() => {
    if (isPlatformBrowser(this.platformId)) {
      const url = this.baseShareUrl();
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    }
    return "";
  })


  protected twitterShareUrl = computed(() => {
    if (isPlatformBrowser(this.platformId)) {
      const url = this.baseShareUrl();
      return "https://twitter.com/intent/tweet?url=" + encodeURIComponent(url);
    }
    return "";
  })

  protected linkedinShareUrl = computed(() => {
    if (isPlatformBrowser(this.platformId)) {
      const url = this.baseShareUrl();
      return "https://www.linkedin.com/shareArticle?mini=true&url=" + encodeURIComponent(url);
    }
    return "";
  })

  protected showOrgLogo = computed(() => {
    const homePageData = this.homePageData();
    if (homePageData != null) {
      if (homePageData.orgLogoUrl != null) {
        return homePageData.orgLogoUrl.length > 0;
      }
    }
    return false;
  });

  protected items = computed(() => {
    const isAuthenticated = this.isAuthenticated();
    const name = this.userName();
    const showShoppingCart = this.showShoppingCart();
    const items: MenuItem[] = [];
    items.push({
      label: "Home",
      icon: this.icons.faHome.iconName,
      command: () => { this.router.navigate(['auction', this.id()]); },
      routerLink: `/auction/${this.id()}`
    });
    items.push({
      label: "Favorites",
      icon: this.icons.faBookmark.iconName,
      command: () => {
        if (this.isAuthenticated()) {
          this.router.navigate(['my-account/favorites'])
        } else {
          this.openLoginModal(() => {
            this.openFavorites();
          });
        }
      }
    });
    items.push({
      separator: true
    });
    if (isAuthenticated) {
      items.push({
        label: `Welcome, ${name}`,
        items: [
          {
            label: 'Account',
            icon: this.icons.faUser.iconName,
            command: () => this.router.navigate(['my-account/account-info']),
          },
          {
            label: 'Password',
            icon: this.icons.faLock.iconName,
            command: () => this.router.navigate(['my-account/password']),
          },
          {
            label: 'Shipping Address',
            icon: this.icons.faMapMarker.iconName,
            command: () => this.router.navigate(['my-account/shipping-address']),

          },
          {
            label: 'Credit Cards',
            icon: this.icons.faCreditCard.iconName,
            command: () => this.router.navigate(['my-account/credit-cards']),
          },
          {
            label: 'Text Messages',
            icon: this.icons.faComment.iconName,
            command: () => this.router.navigate(['my-account/text-messages']),
          },
          {
            label: 'My Searches',
            icon: this.icons.faSearch.iconName,
            command: () => this.router.navigate(['my-account/my-searches']),
          },
          {
            label: 'My Bids',
            icon: this.icons.faShoppingCart.iconName,
            command: () => this.router.navigate(['my-account/my-bids']),
          },
          {
            label: 'My Purchases',
            icon: this.icons.faWallet.iconName,
            command: () => this.router.navigate(['my-account/my-purchases']),
          },
          {
            label: 'My Auctions',
            icon: this.icons.faGavel.iconName,
            command: () => this.router.navigate(['my-account/my-auctions']),
          },
          {
            label: 'My Tickets',
            icon: this.icons.faTicket.iconName,
            command: () => this.router.navigate(['my-account/my-tickets']),
          }
        ]
      });
    }
    if (!isAuthenticated) {
      items.push({
        id: 'mobileSignIn',
        label: 'Sign In',
      });
      items.push({
        id: 'mobileRegister',
        label: 'Register',
      });
    } else {
      items.push({
        id: 'mobileSignOut',
        label: 'Sign out',
      })
    }
    if (showShoppingCart) {
      items.push({
        id: 'mobileShoppingCart',
        label: 'Shopping Cart',
      })
    }
    return items;
  });

  protected showShoppingCart = computed(() => {
    const config = this.auctionConfigSignal();
    if (config != null) {
      return config.isUsingShoppingCart && !this.hideShoppingCart();
    }
    return false;
  })

  openFavorites() {
    this.router.navigate([`/my-account/favorites`]);
  }

  openLoginModal(fun?: () => void) {
    if (this.id() != undefined) {
      if (fun == undefined) {
        this.authService.openLoginModal(LOGIN_TYPE_TOPBAR, () => this.openWelcomeBackModal(), this.id());
      } else {
        this.authService.openLoginModal(LOGIN_TYPE_TOPBAR, fun, this.id());
      }
    }
  }

  openWelcomeBackModal() {
    this.welcomeBackService.openWelcomeBackModal();
  }

  openRegister() {
    if (this.id() != undefined) {
      this.authService.openLoginModal(LOGIN_TYPE_REGISTER, function () { }, this.id());
    }
  }

  logout() {
    this.authService.logout();
  }

  protected openPopupCenter(pageURL: string, title: string, w: number, h: number) {
    if (isPlatformBrowser(this.platformId)) {
      var windowSize = {
        width: w,
        height: h
      };
      var windowLocation = {
        left: ((window.screen.availWidth / 2) - (windowSize.width / 2)),
        top: ((window.screen.availHeight / 2) - (windowSize.height / 2))
      };
      window.open(pageURL, title, 'width=' + w + ', height=' + h + ', left=' + windowLocation.left + ', top=' + windowLocation.top);
    }
  }

  protected getIcon(icon: string) {
    switch (icon) {
      case this.icons.faHome.iconName:
        return this.icons.faHome;
      case this.icons.faBookmark.iconName:
        return this.icons.faBookmark;
      case this.icons.faLock.iconName:
        return this.icons.faLock;
      case this.icons.faMapMarker.iconName:
        return this.icons.faMapMarker;
      case this.icons.faCreditCard.iconName:
        return this.icons.faCreditCard;
      case this.icons.faComment.iconName:
        return this.icons.faComment;
      case this.icons.faSearch.iconName:
        return this.icons.faSearch;
      case this.icons.faShoppingCart.iconName:
        return this.icons.faShoppingCart;
      case this.icons.faWallet.iconName:
        return this.icons.faWallet;
      case this.icons.faGavel.iconName:
        return this.icons.faGavel;
      default:
        return this.icons.faUser;
    }
  }
}
